import React, { useEffect, useRef, useState } from "react";
import { FiTrash2, FiShield, FiPlus } from "react-icons/fi";


export function useDragWindowLike(initial = { x: 0, y: 0 }, opts = {}) {
  const {
    onMove: onMoveCb,
    onDragStart,
    onDragEnd,
    parentRef,     // offset parent (your WORLD layer)
    boundsRef,     // clamp bounds (usually same as parentRef)
    zoomRef,       // current scale ref (e.g., 0.5..2)
  } = opts;

  const [pos, setPos] = useState(initial);
  const posRef = useRef(initial);
  const dragRef = useRef(null); // { dx, dy, parentLeft, parentTop, scale, pointerId }

  useEffect(() => { posRef.current = pos; }, [pos]);

  function beginDrag(e) {
    e.preventDefault();
    e.stopPropagation();

    const cardEl = e.currentTarget.parentElement; // header -> card
    const parentEl = parentRef?.current || cardEl?.offsetParent || document.body;
    const parentRect = parentEl.getBoundingClientRect();
    const scale = zoomRef?.current || 1;

    // pointer in WORLD coords
    const startWorldX = (e.clientX - parentRect.left) / scale;
    const startWorldY = (e.clientY - parentRect.top) / scale;

    // offset from current pos (WORLD)
    const dx = startWorldX - posRef.current.x;
    const dy = startWorldY - posRef.current.y;

    dragRef.current = {
      parentLeft: parentRect.left,
      parentTop: parentRect.top,
      dx,
      dy,
      scale,
      pointerId: e.pointerId,
      cardEl,
    };

    document.body.style.userSelect = "none";
    e.currentTarget.setPointerCapture?.(e.pointerId);

    onDragStart && onDragStart();

    const onPointerMove = (ev) => {
      if (!dragRef.current) return;
      const { parentLeft, parentTop, dx, dy, scale, cardEl } = dragRef.current;

      // convert to WORLD coords every move
      const worldX = (ev.clientX - parentLeft) / scale;
      const worldY = (ev.clientY - parentTop) / scale;

      let x = worldX - dx;
      let y = worldY - dy;

      // clamp to WORLD bounds (unscaled)
      if (boundsRef?.current) {
        const bw = boundsRef.current.clientWidth;
        const bh = boundsRef.current.clientHeight;
        const w = cardEl.offsetWidth;
        const h = cardEl.offsetHeight;
        x = Math.min(Math.max(x, 0), bw - w);
        y = Math.min(Math.max(y, 0), bh - h);
      }

      setPos({ x, y });
      onMoveCb && onMoveCb({ x, y });
    };

    const endDrag = () => {
      dragRef.current = null;
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      onDragEnd && onDragEnd();
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag, { once: true });
    window.addEventListener("pointercancel", endDrag, { once: true });
  }

  useEffect(() => {
    // hard cleanup if component unmounts mid-drag
    return () => {
      dragRef.current = null;
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", () => { });
      window.removeEventListener("pointerup", () => { });
      window.removeEventListener("pointercancel", () => { });
    };
  }, []);

  return { pos, setPos, beginDrag };
}

export default function DraggableConsequenceCard({
  consequence,                       // { ClientID, Text, ... }
  consequenceControls,               // list for this owner

  // callbacks you already have
  removeConsequence,
  updateConsequenceText,
  addConsequenceControl,
  removeConsequenceControl,
  updateConsequenceControlText,
  onConsequenceControlRowClick,      // (ownerCID, ctrl)
  ownerId,
  allowDrop,
  handleDropOnConsequence,
  onHoverChange,                     // (id|null)
  initialPos,
  onDragStart,
  onDragEnd,
  // for live connections (optional)
  canvasRef,
  scaledLayerRef,
  zoomRef,
  reportAnchor,                    // (id, {x,y}) relative to canvas
}) {
  const cardRef = useRef(null);

  const { pos, beginDrag } = useDragWindowLike(initialPos, {
    onMove: ({ x, y }) => reportImmediate(x, y),   // ← use live coords from the hook
    onDragStart,
    onDragEnd,
    parentRef: canvasRef,
    boundsRef: null,
    zoomRef,
  });

  // LEFT-middle anchor for consequence cards
  function reportImmediate(x, y) {
    if (!cardRef.current) return;
    const h = cardRef.current.offsetHeight;  // unscaled
    const anchorX = x;                       // LEFT edge
    const anchorY = y + h / 2;
    reportAnchor && reportAnchor(consequence.ClientID, { x: anchorX, y: anchorY });
  }

  // keep anchor valid after non-drag position changes / resize
  useEffect(() => { reportImmediate(pos.x, pos.y); }, [pos.x, pos.y]); // eslint-disable-line
  useEffect(() => {
    const onResize = () => reportImmediate(pos.x, pos.y);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []); // eslint-disable-line

  function reportNow() {
    if (!cardRef.current || !canvasRef?.current) return;
    const w = cardRef.current.offsetWidth;   // unscaled
    const h = cardRef.current.offsetHeight;  // unscaled
    // pos.x/pos.y are already world coords
    const isThreat = false; // or pass a flag
    const x = isThreat ? (pos.x + w) : pos.x;     // RIGHT middle for threats, LEFT middle for consequences
    const y = pos.y + h / 2;
    reportAnchor && reportAnchor(ownerId, { x, y }); // x,y in world coords
  }



  function handleHeaderPointerDown(e) {
    if (e.button !== 0) return;
    if (e.target.closest("input, textarea, button, [data-no-drag]")) return;
    beginDrag(e);
  }

  return (
    <div
      ref={cardRef}
      data-nopan
      className="absolute z-20 w-[220px] bg-white shadow-sm border rounded-md"
      style={{ left: pos.x, top: pos.y, boxSizing: "border-box", touchAction: "none" }}
      onDragOver={allowDrop}
      onDrop={(e) => handleDropOnConsequence(e, consequence.ClientID)}
      onMouseEnter={() => onHoverChange && onHoverChange(consequence.ClientID)}
      onMouseLeave={() => onHoverChange && onHoverChange(null)}
    >
      {/* HEADER */}
      <div
        className="flex items-center justify-between px-3 py-2 rounded-t-md bg-[#3B3B3B] border-b select-none"
        onPointerDown={(e) => {
          e.stopPropagation();
          handleHeaderPointerDown(e);
        }}
      >
        <span className="font-semibold text-white text-sm">Consequence</span>

        <button
          type="button"
          onClick={() => removeConsequence(consequence.ClientID)}
          title="Remove consequence"
          aria-label="Remove consequence"
          className="text-white hover:text-red-600 text-sm px-2 py-1 rounded hover:bg-gray-200"
          draggable={false}
        >
          <FiTrash2 size={16} />
        </button>
      </div>

      {/* BODY */}
      <div className="p-4 min-h-[80px] flex items-start">
        <textarea
          value={consequence.Text}
          onChange={(e) => updateConsequenceText(consequence.ClientID, e.target.value)}
          placeholder="Consequence description"
          className="w-full bg-transparent outline-none border-none focus:ring-0 text-sm text-gray-900 font-normal leading-snug resize-none overflow-hidden"
          rows={2}
          draggable={false}
        />
      </div>
    </div>
  );
}